import React, { createContext, useState, useCallback, useContext, useMemo } from 'react';
import axios from 'axios';
import download from 'downloadjs';
import { Context } from '@components/Context';
import { useAccountStore } from '@components/dashboard/context/UserDashboardContext';
import { PageOptions, PPCReportPDFContent } from '@contracts';
import getOmsUrl from '@utils/getOmsUrl';
import { trackEvent } from '@utils/tracking';
import useNavigate from '@utils/useNavigate';

interface PDFContextValue {
  pdfLoading: boolean;
  error: Error | null;
  success: boolean;
  pdfData: PPCReportPDFContent | null;
  setPdfData: (data: PPCReportPDFContent) => void;
  generatePPCPdf: () => void;
  generateVmsPdf: (resultId: string) => void;
}

// Initial context
const ReportPDFContext = createContext<PDFContextValue>({
  pdfLoading: false,
  error: null,
  success: false,
  pdfData: null,
  setPdfData: () => {},
  generatePPCPdf: () => {},
  generateVmsPdf: () => {}
});

export const ReportPDFProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state } = useContext(Context);
  const { navigateWrapper } = useNavigate();
  const { firebaseAuth, country } = state;
  const userInfo = useAccountStore((accountState) => accountState.userInfo);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);
  const [pdfData, setPdfData] = useState<PPCReportPDFContent | null>(null);
  const omsUrl = getOmsUrl();

  const generatePPCPdf = useCallback(async () => {
    setPdfLoading(true);
    setError(null);
    setSuccess(false);

    try {
      if (!userInfo.firstName) {
        navigateWrapper(`/dashboard/${PageOptions.AccountSettings}/?setName=true`);
        return;
      }
      const fullName = `${userInfo.firstName} ${userInfo.lastName}`;

      const body = { ...pdfData, customerName: fullName, country };
      const { currentUser } = firebaseAuth;
      const idToken = await currentUser?.getIdToken();
      if (!idToken) throw Error('Failed to load authentication, please refresh & try again');
      const url = `${omsUrl}/health/consultations/pdf`;
      const headers = {
        token: idToken ?? '',
        'Content-Type': 'application/json',
        Accept: 'application/pdf'
      };
      const response = await axios.post(url, JSON.stringify(body), { headers, responseType: 'arraybuffer' });
      const content = response.headers['content-type'];
      download(response.data, `Daye-PPCReport-${fullName.replace(/\s+/g, '_').trim()}.pdf`, content);
      trackEvent('ppc_export_pdf');
      setSuccess(true);
    } catch (err) {
      setError(err);
    } finally {
      setPdfLoading(false);
    }
  }, [userInfo.firstName, userInfo.lastName, firebaseAuth, omsUrl, pdfData]);

  const generateVmsPdf = useCallback(
    async (resultId: string) => {
      setPdfLoading(true);
      setError(null);
      setSuccess(false);

      try {
        if (!userInfo.firstName) {
          navigateWrapper(`/dashboard/${PageOptions.AccountSettings}/?setName=true`);
          return;
        }
        const fullName = `${userInfo.firstName} ${userInfo.lastName}`;

        const body = { ...pdfData, customerName: fullName };
        const { currentUser } = firebaseAuth;
        const idToken = await currentUser?.getIdToken();
        if (!idToken) throw Error('Failed to load authentication, please refresh & try again');
        const url = `${omsUrl}/health/results/${resultId}/pdf`;
        const headers = {
          token: idToken ?? '',
          'Content-Type': 'application/json',
          Accept: 'application/pdf'
        };
        const response = await axios.post(url, JSON.stringify(body), { headers, responseType: 'arraybuffer' });
        const content = response.headers['content-type'];
        download(response.data, `Daye-VMSReport-${resultId}.pdf`, content);
        trackEvent('vms_export_results_pdf');
        setSuccess(true);
      } catch (err) {
        setError(err);
      } finally {
        setPdfLoading(false);
      }
    },
    [userInfo.firstName, userInfo.lastName, firebaseAuth, omsUrl, pdfData]
  );

  const contextValue = useMemo(
    () => ({
      pdfLoading,
      error,
      success,
      pdfData,
      setPdfData,
      generatePPCPdf,
      generateVmsPdf
    }),
    [pdfLoading, error, success, pdfData, generatePPCPdf, generateVmsPdf]
  );

  return <ReportPDFContext.Provider value={contextValue}>{children}</ReportPDFContext.Provider>;
};

export default ReportPDFContext;
